import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import { Box, Typography, Card, Button, IconButton, Chip, Avatar } from '@mui/joy';
import LoadingSkeleton from '../components/LoadingSkeleton';
import EnhancedButton from '../components/EnhancedButton';

const GISProjectMap = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const fetchProject = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('gis_projects')
        .select('name, description, external_map_url, created_at, clients(name)')
        .eq('id', id)
        .single();

      if (error) {
        console.error('Error fetching GIS project:', error);
        setLoading(false);
        return;
      }

      setProject(data);
      setLoading(false);
    };

    fetchProject();
  }, [id]);

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const getClientInitials = (clientName) => {
    if (!clientName) return 'UN';
    return clientName.split(' ').map(word => word[0]).join('').toUpperCase();
  };

  if (loading) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <Card sx={{ m: 2, p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <LoadingSkeleton width="40px" height="40px" />
            <Box sx={{ flex: 1 }}>
              <LoadingSkeleton width="200px" height="24px" />
              <LoadingSkeleton width="100px" height="16px" />
            </Box>
            <LoadingSkeleton width="80px" height="32px" />
          </Box>
        </Card>
        <Box sx={{ flex: 1, m: 2 }}>
          <LoadingSkeleton height="100%" />
        </Box>
      </Box>
    );
  }

  if (!project) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Card sx={{ p: 4, textAlign: 'center' }}>
          <Typography level="h4" sx={{ mb: 2, color: 'danger.500' }}>
            Project Not Found
          </Typography>
          <Typography level="body-sm" sx={{ mb: 3, color: 'text.secondary' }}>
            The GIS project you're looking for doesn't exist or has been removed.
          </Typography>
          <EnhancedButton onClick={() => navigate('/gis-projects')}>
            Back to Projects
          </EnhancedButton>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100vh',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.body',
      }}
    >
      {/* Header Controls */}
      {!isFullscreen && (
        <Card 
          sx={{ 
            m: 2, 
            p: 2,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid',
            borderColor: 'success.200',
            zIndex: 1000,
          }}
          className="fade-in"
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar 
                sx={{ 
                  bgcolor: '#00A884',
                  color: 'white',
                  fontWeight: 700
                }}
              >
                {getClientInitials(project.clients?.name)}
              </Avatar>
              
              <Box>
                <Typography level="h4" sx={{ color: 'success.700', mb: 0.5 }}>
                  {project.name || 'Untitled Project'}
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  {project.clients?.name && (
                    <Chip size="sm" variant="soft" color="primary">
                      {project.clients.name}
                    </Chip>
                  )}
                  
                  <Chip 
                    size="sm" 
                    variant="soft" 
                    color="success"
                    startDecorator={
                      <span className="material-symbols-outlined" style={{ fontSize: '14px', fontFamily: 'Material Symbols Outlined' }}>
                        schedule
                      </span>
                    }
                  >
                    {new Date(project.created_at).toLocaleDateString()}
                  </Chip>
                  
                  <Chip 
                    size="sm" 
                    variant="solid" 
                    color="success"
                    startDecorator={
                      <span className="material-symbols-outlined" style={{ fontSize: '14px', fontFamily: 'Material Symbols Outlined' }}>
                        map
                      </span>
                    }
                  >
                    Interactive Map
                  </Chip>
                </Box>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <EnhancedButton
                variant="outlined"
                size="sm"
                onClick={() => navigate('/gis-projects')}
                startDecorator={
                  <span className="material-symbols-outlined" style={{ fontSize: '16px', fontFamily: 'Material Symbols Outlined' }}>
                    arrow_back
                  </span>
                }
              >
                Back
              </EnhancedButton>
              
              <EnhancedButton
                variant="outlined"
                size="sm"
                onClick={toggleFullscreen}
                startDecorator={
                  <span className="material-symbols-outlined" style={{ fontSize: '16px', fontFamily: 'Material Symbols Outlined' }}>
                    fullscreen
                  </span>
                }
              >
                Fullscreen
              </EnhancedButton>
              
              {project.external_map_url && (
                <EnhancedButton
                  gradient
                  size="sm"
                  onClick={() => window.open(project.external_map_url, '_blank')}
                  startDecorator={
                    <span className="material-symbols-outlined" style={{ fontSize: '16px', fontFamily: 'Material Symbols Outlined' }}>
                      open_in_new
                    </span>
                  }
                >
                  Open in New Tab
                </EnhancedButton>
              )}
            </Box>
          </Box>

          {project.description && (
            <Typography 
              level="body-sm" 
              sx={{ 
                mt: 2, 
                p: 2, 
                bgcolor: 'success.25',
                borderRadius: 'sm',
                border: '1px solid',
                borderColor: 'success.200'
              }}
            >
              {project.description}
            </Typography>
          )}
        </Card>
      )}

      {/* Map Container */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          position: 'relative',
          m: isFullscreen ? 0 : 2,
          mt: isFullscreen ? 0 : 0,
        }}
      >
        {/* Fullscreen Controls Overlay */}
        {isFullscreen && (
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 1000,
              display: 'flex',
              gap: 1,
            }}
          >
            <IconButton
              variant="solid"
              color="neutral"
              onClick={toggleFullscreen}
              sx={{
                bgcolor: 'rgba(0, 0, 0, 0.7)',
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(0, 0, 0, 0.8)',
                },
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                fullscreen_exit
              </span>
            </IconButton>
          </Box>
        )}

        {project.external_map_url ? (
          <Card
            sx={{
              flex: 1,
              overflow: 'hidden',
              border: '2px solid',
              borderColor: 'success.200',
              boxShadow: '0 4px 20px rgba(0, 168, 132, 0.1)',
            }}
          >
            <iframe
              title={project.name}
              src={project.external_map_url}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                display: 'block',
              }}
            />
          </Card>
        ) : (
          <Card 
            sx={{ 
              flex: 1, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              border: '2px dashed',
              borderColor: 'neutral.300',
            }}
          >
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <span 
                className="material-symbols-outlined" 
                style={{ 
                  fontSize: '64px', 
                  color: '#6c757d',
                  fontFamily: 'Material Symbols Outlined'
                }}
              >
                map
              </span>
              <Typography level="h4" sx={{ mt: 2, mb: 1, color: 'neutral.500' }}>
                No Map Available
              </Typography>
              <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                This project doesn't have an external map URL configured yet.
              </Typography>
            </Box>
          </Card>
        )}
      </Box>
    </Box>
  );
};

export default GISProjectMap;